<template>
  <div>
    <div class="card card-custom">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-6">
            <input type="text" v-model="searchText" class="form-control"
                   placeholder="Search Name, CNIC, NTN, City..."
                   @keyup.enter="searchData">
          </div>
          <div class="col-md-2">
            <button class="btn btn-primary" @click="searchData" :disabled="isSearching">
              <span v-if="!isSearching">Search</span>
              <span v-else><i class="fas fa-spinner fa-spin"></i> Searching...</span>
            </button>
          </div>
        </div>

        <!-- ── Progress Bar (visible while searching) ── -->
        <div v-if="isSearching || searchDone" class="border rounded p-3 bg-light mt-2">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>Search Progress</strong>
            <span class="badge" style="background:#2d5c88;color:#fff;font-size:0.85rem;">{{ overallPercent }}%</span>
          </div>

          <!-- Overall bar -->
          <div class="progress mb-3" style="height:12px;">
            <div class="progress-bar"
                 role="progressbar"
                 :style="{
                   width: overallPercent + '%',
                   background: overallPercent === 100 ? '#28a745' : '#2d5c88',
                   transition: 'width 0.3s ease'
                 }"></div>
          </div>

          <!-- Per-step cards -->
          <div class="row g-2">
            <div class="col-md-2" v-for="step in progressSteps" :key="step.key">
              <div class="border rounded p-2 text-center"
                   :style="{
                     borderColor: step.status === 'done'   ? '#28a745'
                                : step.status === 'active' ? '#2d5c88' : '#dee2e6',
                     background:  step.status === 'done'   ? '#f0fbf3'
                                : step.status === 'active' ? '#eaf3fb' : '#fff'
                   }">
                <div style="font-size:1.1rem;">
                  <i v-if="step.status === 'done'" class="fas fa-check-circle" style="color:#28a745;"></i>
                  <i v-else-if="step.status === 'active'" class="fas fa-circle-notch fa-spin" style="color:#2d5c88;"></i>
                  <i v-else class="fas fa-circle" style="color:#ccc;"></i>
                </div>
                <div class="small fw-bold mt-1" style="color:#2d5c88;font-size:0.72rem;">{{ step.label }}</div>
                <div class="small text-muted" style="font-size:0.68rem;">
                  <span v-if="step.status === 'done'">{{ step.found }} found</span>
                  <span v-else-if="step.status === 'active'">Searching...</span>
                  <span v-else>Waiting</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- ── End Progress Bar ── -->

        <div v-if="searched" class="border rounded p-3 bg-light mt-2">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>Filter Results</strong>
            <button class="btn btn-sm btn-secondary" @click="clearFilters">Clear Filters</button>
          </div>
          <div class="row g-2">
            <div class="col-md-2">
              <label class="form-label mb-1 small">Name</label>
              <input type="text" v-model="filters.name" class="form-control form-control-sm" placeholder="Filter by name">
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1 small">CNIC No</label>
              <input type="text" v-model="filters.cnic" class="form-control form-control-sm" placeholder="Filter by CNIC">
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1 small">NTN No</label>
              <input type="text" v-model="filters.ntn" class="form-control form-control-sm" placeholder="Filter by NTN">
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1 small">GST No</label>
              <input type="text" v-model="filters.gst" class="form-control form-control-sm" placeholder="Filter by GST">
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1 small">Client Code</label>
              <input type="text" v-model="filters.clientCode" class="form-control form-control-sm" placeholder="Filter by code">
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1 small">City</label>
              <input type="text" v-model="filters.city" class="form-control form-control-sm" placeholder="Filter by city">
            </div>
            <div class="col-md-4 mt-2">
              <label class="form-label mb-1 small">Address</label>
              <input type="text" v-model="filters.address" class="form-control form-control-sm" placeholder="Filter by address">
            </div>
          </div>
        </div>

        <div v-if="searched && !filteredClientList.length && !filteredBeneficialList.length && !filteredDirectorList.length && !filteredSignatoryList.length && !filteredBlockClientList.length && !filteredShareHolderList.length"
             class="alert alert-warning mt-3">
          No record found.
        </div>
      </div>
    </div>

    <!-- Client Profile -->
    <div class="card mt-3">
      <div class="card-header"><h5>Client Profile</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="clientTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Code</th>
              <th>Name</th>
              <th>Partner Type</th>
              <th>City</th>
              <th>Business Nature</th>
              <th>Address</th>
              <th>Mobile No</th>
              <th>CNIC</th>
              <th>GST</th>
              <th>NTN</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredClientList" :key="item.Code">
              <td>
                <span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">
                  {{ item._matchedBy }}
                </span>
              </td>
              <td>{{item.BP_CODE_NEW}}</td>
              <td>{{item.BP_DSC}}</td>
              <td>{{item.PTR_TYPE}}</td>
              <td>{{item.City}}</td>
              <td>{{item.BN_DESC}}</td>
              <td>{{item.Addresses}}</td>
              <td>{{item.BP_MOBILE}}</td>
              <td>{{item.BP_NIC_NO}}</td>
              <td>{{item.BP_GST_NO}}</td>
              <td>{{item.BP_NTN_NO}}</td>
              <td><input type="checkbox" :checked="item.BP_ACTIVE == 1" disabled></td>
              <td>
                <button class="btn btn-primary btn-sm" @click="editInsured(item.BP_ID, item.PTR_ID)"><i class="fas fa-pencil-alt" /></button>
                <button class="btn btn-success btn-sm" @click="printKyc(item.BP_ID, item.PTR_ID)"><i class="fas fa-download" /></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Director Info -->
    <div class="card mt-3">
      <div class="card-header"><h5>Director Info</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="directorTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Code</th>
              <th>Client Name</th>
              <th>Director Name</th>
              <th>Address</th>
              <th>CNIC</th>
              <th>Issue Date</th>
              <th>Expiry Date</th>
              <th>PEP</th>
              <th>Self Position</th>
              <th>Association</th>
              <th>Status Date</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredDirectorList" :key="item.BO_NAME + item.BP_CODE_NEW">
              <td>
                <span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">
                  {{ item._matchedBy }}
                </span>
              </td>
              <td>{{item.BP_CODE_NEW}}</td>
              <td>{{item.BP_DSC}}</td>
              <td>{{item.BO_NAME}}</td>
              <td>{{item.BO_ADDRESS}}</td>
              <td>{{item.BO_CNICPASSNO}}</td>
              <td>{{item.BO_CNICPASS_ISSUEDTE}}</td>
              <td>{{item.BO_CNICPASS_EXPDTE}}</td>
              <td>{{item.BO_PEPYN}}</td>
              <td>{{item.BO_IFYESSELFPOSITION}}</td>
              <td>{{item.BO_ASSOCRELWITHPEP}}</td>
              <td>{{item.BO_STATUSDATE}}</td>
              <td><input type="checkbox" :checked="item.BO_ACTIVE == 1" disabled></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Beneficial Ownership -->
    <div class="card mt-3">
      <div class="card-header"><h5>Beneficial Ownership</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="beneficialTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Code</th>
              <th>Client Name</th>
              <th>Beneficiary Name</th>
              <th>Address</th>
              <th>CNIC</th>
              <th>Issue Date</th>
              <th>Expiry Date</th>
              <th>PEP</th>
              <th>Self Position</th>
              <th>Association</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredBeneficialList" :key="item.BO_NAME + item.BP_CODE_NEW">
              <td>
                <span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">
                  {{ item._matchedBy }}
                </span>
              </td>
              <td>{{item.BP_CODE_NEW}}</td>
              <td>{{item.BP_DSC}}</td>
              <td>{{item.BO_NAME}}</td>
              <td>{{item.BO_ADDRESS}}</td>
              <td>{{item.BO_CNICPASSNO}}</td>
              <td>{{item.BO_CNICPASS_ISSUEDTE}}</td>
              <td>{{item.BO_CNICPASS_EXPDTE}}</td>
              <td>{{item.BO_PEPYN}}</td>
              <td>{{item.BO_IFYESSELFPOSITION}}</td>
              <td>{{item.BO_ASSOCRELWITHPEP}}</td>
              <td><input type="checkbox" :checked="item.BO_ACTIVE == 1" disabled></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Signatory -->
    <div class="card mt-3">
      <div class="card-header"><h5>Signatory</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="signatoryTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Code</th>
              <th>Client Name</th>
              <th>Signatory Name</th>
              <th>CNIC</th>
              <th>Designation</th>
              <th>BOR</th>
              <th>Status</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredSignatoryList" :key="item.BO_NAME + item.BP_CODE_NEW">
              <td>
                <span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">
                  {{ item._matchedBy }}
                </span>
              </td>
              <td>{{item.BP_CODE_NEW}}</td>
              <td>{{item.BP_DSC}}</td>
              <td>{{item.BO_NAME}}</td>
              <td>{{item.BO_CNICPASSNO}}</td>
              <td>{{item.BO_DESIGNATION}}</td>
              <td>{{item.BO_BORYN}}</td>
              <td>{{item.BO_STATUS}}</td>
              <td><input type="checkbox" :checked="item.BO_ACTIVE == 1" disabled></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Block Accounts Client -->
    <div class="card mt-3">
      <div class="card-header"><h5>Block Accounts Client</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="blockClientsTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Name</th>
              <th>Father Name</th>
              <th>CNIC No</th>
              <th>SRO NO</th>
              <th>DATE OF SCR</th>
              <th>Black List By</th>
              <th>SECP Email Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredBlockClientList" :key="item.SR_NO">
              <td>
                <span class="badge" style="background:#fff0f0;color:#c0392b;border:1px solid #f5b7b1;font-size:0.72rem;">
                  {{ item._matchedBy }}
                </span>
              </td>
              <td>{{item.CLIENT_NAME}}</td>
              <td>{{item.FATHERNAME}}</td>
              <td>{{item.CNIC_NO}}</td>
              <td>{{item.SRO_NO}}</td>
              <td>{{ formatDate(item.DATE_OF_SCR) }}</td>
              <td>{{item.BLACK_LIST_BY}}</td>
              <td>{{ formatDate(item.SECP_EMAIL_DATE) }}</td>
              <td>{{item.STATUS}}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card mt-3">
      <div class="card-header"><h5>CDC Shareholders</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="cdcTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Folio No</th>
              <th>Name</th>
              <th>Address</th>
              <th>Share Held</th>
              <th>CNIC</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>CDC</th>
              <th>As On Date</th>
              <th>Insert Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredShareHolderList" :key="item.FOLIO_NO + item.CNIC_NO">
              <td>
                <span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">
                  {{ item._matchedBy }}
                </span>
              </td>
              <td>{{item.FOLIO_NO}}</td>
              <td>{{item.NAME}}</td>
              <td>{{item.ADDRESS}}</td>
              <td>{{formatNumber(item.SHARE_HELD)}}</td>
              <td>{{item.CNIC_NO}}</td>
              <td>{{item.MOBILE_NO}}</td>
              <td>{{item.EMAIL_ADDRESS}}</td>
              <td>{{item.CDC_YN}}</td>
              <td>{{ formatDate(item.AS_ON_DATE) }}</td>
              <td>{{ formatDate(item.INSERT_DATE) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
  import axios from 'axios';

  var PROGRESS_STEPS = [
    { key: 'clientProfile', label: 'Client Profile', apiKey: 'clientProfile' },
    { key: 'directorInfo', label: 'Director Info', apiKey: 'directorInfo' },
    { key: 'beneficialOwnership', label: 'Beneficial Ownership', apiKey: 'beneficialOwnership' },
    { key: 'signatoryInfo', label: 'Signatory Info', apiKey: 'signatoryInfo' },
    { key: 'blockAccountsClient', label: 'Block Accounts', apiKey: 'blockAccountsClient' },
    { key: 'cdcShareholders', label: 'CDC Shareholders', apiKey: 'cdcShareholders' }
  ];

  export default {
    data() {
      return {
        searchText: "",
        clientProfileList: [],
        beneficialList: [],
        directorList: [],
        signatoryList: [],
        blockClientList: [],
        cdcShareHolderList: [],
        searched: false,

        isSearching: false,
        searchDone: false,
        progressSteps: [],
        progressTimer: null,

        filters: {
          name: "",
          cnic: "",
          ntn: "",
          gst: "",
          clientCode: "",
          city: "",
          address: ""
        }
      }
    },

    computed: {
      overallPercent() {
        if (!this.progressSteps.length) return 0;
        var total = 0;
        for (var i = 0; i < this.progressSteps.length; i++) {
          total += this.progressSteps[i].percent;
        }
        return Math.round(total / this.progressSteps.length);
      },

      filteredClientList() {
        return this.clientProfileList.filter(item => {
          const f = this.filters;
          return (
            (!f.name || (item.BP_DSC || "").toUpperCase().includes(f.name.toUpperCase())) &&
            (!f.cnic || (item.BP_NIC_NO || "").toUpperCase().includes(f.cnic.toUpperCase())) &&
            (!f.ntn || (item.BP_NTN_NO || "").toUpperCase().includes(f.ntn.toUpperCase())) &&
            (!f.gst || (item.BP_GST_NO || "").toUpperCase().includes(f.gst.toUpperCase())) &&
            (!f.clientCode || (item.BP_CODE_NEW || "").toUpperCase().includes(f.clientCode.toUpperCase())) &&
            (!f.city || (item.City || "").toUpperCase().includes(f.city.toUpperCase())) &&
            (!f.address || (item.Addresses || "").toUpperCase().includes(f.address.toUpperCase()))
          );
        });
      },
      filteredDirectorList() {
        return this.directorList.filter(item => {
          const f = this.filters;
          const nameMatch = !f.name || [item.BO_NAME, item.BP_DSC]
            .some(v => (v || "").toUpperCase().includes(f.name.toUpperCase()));
          return (
            nameMatch &&
            (!f.cnic || (item.BO_CNICPASSNO || "").toUpperCase().includes(f.cnic.toUpperCase())) &&
            (!f.address || (item.BO_ADDRESS || "").toUpperCase().includes(f.address.toUpperCase()))
          );
        });
      },
      filteredBeneficialList() {
        return this.beneficialList.filter(item => {
          const f = this.filters;
          const nameMatch = !f.name || [item.BO_NAME, item.BP_DSC]
            .some(v => (v || "").toUpperCase().includes(f.name.toUpperCase()));
          return (
            nameMatch &&
            (!f.cnic || (item.BO_CNICPASSNO || "").toUpperCase().includes(f.cnic.toUpperCase())) &&
            (!f.address || (item.BO_ADDRESS || "").toUpperCase().includes(f.address.toUpperCase()))
          );
        });
      },
      filteredSignatoryList() {
        return this.signatoryList.filter(item => {
          const f = this.filters;
          const nameMatch = !f.name || [item.BO_NAME, item.BP_DSC]
            .some(v => (v || "").toUpperCase().includes(f.name.toUpperCase()));
          return (
            nameMatch &&
            (!f.cnic || (item.BO_CNICPASSNO || "").toUpperCase().includes(f.cnic.toUpperCase()))
          );
        });
      },
      filteredBlockClientList() {
        return this.blockClientList.filter(item => {
          const f = this.filters;
          return (
            (!f.name || (item.CLIENT_NAME || "").toUpperCase().includes(f.name.toUpperCase())) &&
            (!f.cnic || (item.CNIC_NO || "").toUpperCase().includes(f.cnic.toUpperCase())) &&
            (!f.city || (item.City || "").toUpperCase().includes(f.city.toUpperCase()))
          );
        });
      },
      filteredShareHolderList() {
        return this.cdcShareHolderList.filter(item => {
          const f = this.filters;
          return (
            (!f.name || (item.NAME || "").toUpperCase().includes(f.name.toUpperCase())) &&
            (!f.cnic || (item.CNIC_NO || "").toUpperCase().includes(f.cnic.toUpperCase()))
          );
        });
      }
    },

    methods: {
      clearFilters() {
        this.filters = { name: "", cnic: "", ntn: "", gst: "", clientCode: "", city: "", address: "" };
      },
      destroyDataTable(id) {
        if ($.fn.DataTable.isDataTable(`#${id}`)) {
          $(`#${id}`).DataTable().destroy();
        }
      },
      initDataTables() {
        ['clientTable', 'beneficialTable', 'directorTable', 'signatoryTable', 'blockClientsTable', 'cdcTable'].forEach(id => {
          $(`#${id}`).DataTable({
            destroy: true,
            responsive: true,
            language: { emptyTable: "No data available" }
          });
        });
      },

      _startProgress() {
        var self = this;

        self.progressSteps = PROGRESS_STEPS.map(function (s) {
          return { key: s.key, label: s.label, apiKey: s.apiKey, status: 'pending', percent: 0, found: 0 };
        });

        self.isSearching = true;
        self.searchDone = false;

        var STEP_DURATION = 600;  
        var stepIndex = 0;

        function activateStep() {
          if (stepIndex >= self.progressSteps.length) return;

          if (stepIndex > 0) {
            self.progressSteps[stepIndex - 1].status = 'done';
            self.progressSteps[stepIndex - 1].percent = 100;
          }

          self.progressSteps[stepIndex].status = 'active';

          var start = Date.now();
          var step = self.progressSteps[stepIndex];

          var tick = setInterval(function () {
            var elapsed = Date.now() - start;
            step.percent = Math.min(95, Math.round((elapsed / STEP_DURATION) * 95));

            if (elapsed >= STEP_DURATION) {
              clearInterval(tick);
              stepIndex++;
              activateStep();
            }
          }, 30);

          self.progressTimer = tick;
        }

        activateStep();
      },

      _finishProgress(data) {
        var self = this;

        if (self.progressTimer) { clearInterval(self.progressTimer); }

        PROGRESS_STEPS.forEach(function (s, i) {
          var step = self.progressSteps[i];
          step.status = 'done';
          step.percent = 100;
          step.found = (data[s.apiKey] || []).length;
        });

        self.isSearching = false;
        self.searchDone = true;
      },

      searchData() {
        var self = this;
        startReq();
        self.clearFilters();
        self.destroyDataTable('clientTable');
        self.destroyDataTable('beneficialTable');
        self.destroyDataTable('directorTable');
        self.destroyDataTable('signatoryTable');
        self.destroyDataTable('blockClientsTable');
        self.destroyDataTable('cdcTable');

        self._startProgress();

        axios.get("/api/UICApi/ClientAdvanceSearch", {
          params: { search: self.searchText }
        })
          .then(res => {
            endReq();
            var data = res.data;

            self.clientProfileList = (data.clientProfile || []).map(function (item) {
              return Object.assign({}, item, { _matchedBy: self.searchText });
            });
            self.beneficialList = (data.beneficialOwnership || []).map(function (item) {
              return Object.assign({}, item, { _matchedBy: self.searchText });
            });
            self.directorList = (data.directorInfo || []).map(function (item) {
              return Object.assign({}, item, { _matchedBy: self.searchText });
            });
            self.signatoryList = (data.signatoryInfo || []).map(function (item) {
              return Object.assign({}, item, { _matchedBy: self.searchText });
            });
            self.blockClientList = (data.blockAccountsClient || []).map(function (item) {
              return Object.assign({}, item, { _matchedBy: self.searchText });
            });
            self.cdcShareHolderList = (data.cdcShareholders || []).map(function (item) {
              return Object.assign({}, item, { _matchedBy: self.searchText });
            });
            self.searched = true;

            self._finishProgress(data);

            self.$nextTick(() => {
              self.initDataTables();
            });
          })
          .catch(() => {
            endReq();
            self._finishProgress({});
          });
      },

      formatDate(date) {
        return date ? new Date(date).toISOString().split('T')[0] : '';
      },
      formatNumber(value) {
        return value != null ? Number(value).toLocaleString() : '';
      },
      editInsured(id, ptrId) {
        if (ptrId === 17 || ptrId === 1) {
          window.open("/uic/IndividualProfile?id=" + id);
        } else {
          window.open("/uic/CorporateProfile?id=" + id);
        }
      },
      printKyc(id, ptrId) {
        if (ptrId === 17 || ptrId === 1) {
          window.open("/UIC/IndividualClientPrint?id=" + id);
        } else {
          window.open("/UIC/CorporateClientPrint?id=" + id);
        }
      }
    }
  }
</script>

<style scoped>
  h5 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 15px;
    font-weight: 600;
    border-bottom: 2px solid #2d5c88;
    padding-bottom: 5px;
  }
</style>



// WEBPACK FOOTER //
// src/components/ims/ClientAdvanceSearch.vue